// w46 To'rt fasl (Four Seasons) — catalog preview card.
//
// The full template is one continuous vertical scroll that lives through
// spring, summer, autumn and winter around a single bare tree. The catalog
// card compresses that journey into one still frame: four horizontal
// season bands — blossom pink (spring), summer green, autumn amber, winter
// lilac — blending softly into one another top to bottom (the same four
// dot colors the "Kun dasturi" timeline cycles through), a scatter of tiny
// season-matched dots (falling petals in spring, drifting leaves in
// autumn, snowflakes in winter), and a cream center badge holding the
// couple's initials, names and a date rule. Uses the site's already-loaded
// serif stack (via `font`) for the names only — no new font import.
import { type LayoutCtx } from "./_shared";

// Deterministic pseudo-random helper so the card renders identically on
// every paint (no client/server mismatch, no re-shuffle on re-render).
function seeded(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

// The four season dot colors, in scroll order — matches the "Kun dasturi"
// timeline dots in fasllar.html.
const SPRING = "#f6a8c4";
const SUMMER = "#4f8f4d";
const AUTUMN = "#ef953f";
const WINTER = "#b9c9ec";

// Small scatters of season-matched particles confined to their own band.
const PETAL_COUNT = 6; // spring band: 0–25%
const LEAF_COUNT = 5; // autumn band: 50–75%
const SNOW_COUNT = 8; // winter band: 75–100%

/** Couple initials with the design's middot form ("T · M"); a single name
 *  collapses to a single initial (no middot). Splits on the common couple
 *  separators the editor's `names` field actually produces. */
function initials(names: string): string {
  const parts = names.split(/\s*(?:&|\+|,|\bva\b|\band\b|\bи\b)\s*/i).filter((p) => p.trim());
  const first = (parts[0] ?? "").trim().charAt(0).toUpperCase();
  if (parts.length < 2) return first;
  const second = parts[1].trim().charAt(0).toUpperCase();
  return `${first} · ${second}`;
}

export function FasllarPreviewLayout({ names, date, font }: LayoutCtx) {
  const cream = "#fdf6ef";
  const ink = "#4a3b33";

  const petals = Array.from({ length: PETAL_COUNT }, (_, i) => ({
    left: 8 + seeded(i * 3.1 + 1) * 84,
    top: 3 + seeded(i * 7.7 + 2) * 18,
    size: 3 + seeded(i * 5.3 + 3) * 3,
    rot: seeded(i * 4.1 + 4) * 360,
  }));
  const leaves = Array.from({ length: LEAF_COUNT }, (_, i) => ({
    left: 6 + seeded(i * 9.2 + 11) * 88,
    top: 53 + seeded(i * 2.6 + 12) * 18,
    size: 4 + seeded(i * 6.4 + 13) * 3,
    rot: seeded(i * 8.8 + 14) * 360,
  }));
  const snow = Array.from({ length: SNOW_COUNT }, (_, i) => ({
    left: 4 + seeded(i * 1.9 + 21) * 92,
    top: 78 + seeded(i * 3.3 + 22) * 19,
    size: 2 + seeded(i * 5.7 + 23) * 2,
    opacity: 0.5 + seeded(i * 2.2 + 24) * 0.5,
  }));

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        // Spring → summer → autumn → winter, blending softly at each seam
        // — the same journey the live scroll's body background lerps
        // through, keyed here to fixed band midpoints for the still card.
        background: `linear-gradient(180deg,
          ${SPRING} 0%, ${SPRING} 22%,
          ${SUMMER} 28%, ${SUMMER} 47%,
          ${AUTUMN} 53%, ${AUTUMN} 72%,
          ${WINTER} 78%, ${WINTER} 100%)`,
        overflow: "hidden",
      }}
    >
      {/* Falling petals — spring band. */}
      {petals.map((p, i) => (
        <div
          key={`petal-${i}`}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size * 1.4,
            borderRadius: "70% 30% 70% 30%",
            background: "rgba(255,255,255,0.75)",
            transform: `rotate(${p.rot}deg)`,
          }}
        />
      ))}

      {/* Drifting leaves — autumn band. */}
      {leaves.map((l, i) => (
        <div
          key={`leaf-${i}`}
          style={{
            position: "absolute",
            left: `${l.left}%`,
            top: `${l.top}%`,
            width: l.size,
            height: l.size * 1.3,
            borderRadius: "0% 70% 0% 70%",
            background: "rgba(255,255,255,0.6)",
            transform: `rotate(${l.rot}deg)`,
          }}
        />
      ))}

      {/* Falling snowflakes — winter band. */}
      {snow.map((s, i) => (
        <div
          key={`snow-${i}`}
          style={{
            position: "absolute",
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: "#ffffff",
            opacity: s.opacity,
          }}
        />
      ))}

      {/* Cream center badge carrying initials + names + date. */}
      <div
        style={{
          position: "absolute",
          left: "12%",
          right: "12%",
          top: "50%",
          transform: "translateY(-50%)",
          background: cream,
          border: `1px solid ${AUTUMN}55`,
          borderRadius: 10,
          padding: "9% 8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: "0 4px 14px rgba(74,59,51,0.18)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.18em",
            color: ink,
            opacity: 0.75,
            marginBottom: "6%",
          }}
        >
          {initials(names)}
        </div>

        <div
          style={{
            fontFamily: font,
            fontSize: 13,
            lineHeight: 1.25,
            color: ink,
            marginBottom: "8%",
          }}
        >
          {names}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "8%" }}>
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: AUTUMN }} />
          <div style={{ width: 34, height: 1, background: `${ink}33` }} />
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: AUTUMN }} />
        </div>

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.24em",
            color: ink,
            opacity: 0.8,
          }}
        >
          {date.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
